// Types for AI Briefings data
export interface KPI {
  label: string
  value: number | string
  change?: number
  trend?: 'up' | 'down' | 'stable'
}

export interface TrendDataPoint {
  date: string
  value: number
}

export interface Driver {
  title: string
  impact: string
  change: number
}

export interface Anomaly {
  id: string
  title: string
  severity: 'Bassa' | 'Media' | 'Alta'
  metric: string
  expected: number
  actual: number
  sparkline: number[]
  explanation: string
  suggestedFilters?: string[]
}

export interface FunnelStep {
  label: string
  value: number
  dropoff?: number
}

export interface PointsEconomy {
  diamond: {
    earned: number
    spent: number
    net: number
    trend: TrendDataPoint[]
  }
  star: {
    earned: number
    spent: number
    net: number
    trend: TrendDataPoint[]
  }
  topSources: { label: string; value: number }[]
  topSinks: { label: string; value: number }[]
}

export interface ProofsPipeline {
  submitted: number
  inReview: number
  approved: number
  rejected: number
  avgReviewTime: string
  rejectionReasons: { reason: string; count: number }[]
  trend: {
    date: string
    submitted: number
    approved: number
    rejected: number
  }[]
}

export interface RewardPerformance {
  claims: number
  utilizations: number
  conversionRate: number
  topRewards: { name: string; claims: number; utilizations: number }[]
  topPartners: { name: string; conversionRate: number }[]
  lowPerforming: { name: string; issue: string }[]
}

export interface PartnerPerformance {
  rankings: { name: string; score: number; trend: number }[]
  actions: {
    partner: string
    action: string
    priority: 'Alta' | 'Media' | 'Bassa'
  }[]
  trend: TrendDataPoint[]
}

export interface LMSPulse {
  activeUsers: number
  completions: number
  passRate: number
  dropoffModules: { module: string; dropoffRate: number; enrollments: number }[]
  trend: TrendDataPoint[]
}

export interface CommunityData {
  posts: number
  comments: number
  reactions: number
  hotTopics: { tag: string; count: number }[]
}

export interface EventsData {
  participation: number
  completionRate: number
  upcomingRegistrations: number
  bestChallenges: {
    name: string
    participants: number
    completionRate: number
  }[]
}

export interface RetentionData {
  wau: number
  mau: number
  d7Retention: number
  d30Retention: number
  overlap: {
    appOnly: number
    lmsOnly: number
    rewardsOnly: number
    appLms: number
    appRewards: number
    lmsRewards: number
    all: number
  }
  cohortTrend: { date: string; retention: number }[]
}

export interface DataFreshness {
  sources: {
    name: string
    lastUpdate: string
    status: 'ok' | 'warning' | 'error'
    coverage: number
  }[]
}

// Mock data generators
const generateTrendData = (
  days: number,
  baseValue: number,
  variance: number,
): TrendDataPoint[] => {
  const data: TrendDataPoint[] = []
  const today = new Date()

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    const value = baseValue + Math.random() * variance - variance / 2
    data.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(value),
    })
  }

  return data
}

// Executive Brief Data
export const getExecutiveBriefData = () => ({
  kpis: [
    {
      label: 'Utenti Attivi',
      value: '4,235',
      change: 12.5,
      trend: 'up' as const,
    },
    {
      label: 'Azioni Completate',
      value: '18,492',
      change: 8.3,
      trend: 'up' as const,
    },
    {
      label: 'Prove Approvate',
      value: '3,847',
      change: 15.7,
      trend: 'up' as const,
    },
    {
      label: 'Diamond Netti',
      value: '+12,450',
      change: 22.1,
      trend: 'up' as const,
    },
    {
      label: 'Star Netti',
      value: '+8,920',
      change: 18.4,
      trend: 'up' as const,
    },
    { label: 'Riscatti', value: '1,234', change: 5.2, trend: 'up' as const },
    { label: 'Utilizzi', value: '892', change: -3.1, trend: 'down' as const },
    {
      label: 'Corsi Completati',
      value: '567',
      change: 28.9,
      trend: 'up' as const,
    },
  ],
  narrative:
    'Crescita sostenuta su tutti i fronti. Gli utenti attivi sono aumentati del 12.5% grazie alle nuove challenge lanciate. Il tasso di approvazione delle prove è migliorato significativamente.',
  drivers: [
    {
      title: 'Challenge "Plastic Free"',
      impact: '+450 utenti attivi',
      change: 18.2,
    },
    { title: 'Nuovi partner premium', impact: '+280 riscatti', change: 22.8 },
    {
      title: 'Corso "Sostenibilità 101"',
      impact: '+120 completamenti',
      change: 45.3,
    },
  ],
  trend: generateTrendData(30, 4000, 400),
})

// Anomalies Data
export const getAnomaliesData = (): Anomaly[] => [
  {
    id: 'anom-001',
    title: 'Calo improvviso utilizzi premi',
    severity: 'Alta',
    metric: 'Utilizzi',
    expected: 950,
    actual: 892,
    sparkline: [920, 935, 940, 955, 948, 892, 895],
    explanation:
      'Il tasso di utilizzo dei premi riscattati è sceso del 6.1% negli ultimi 7 giorni. Analisi suggerisce problemi di comunicazione con 2 partner chiave (NaturaSì, EcoCasa) che non hanno confermato gli utilizzi in tempo.',
    suggestedFilters: [
      'Partner: NaturaSì',
      'Partner: EcoCasa',
      'Periodo: Ultimi 7 giorni',
    ],
  },
  {
    id: 'anom-002',
    title: 'Picco rifiuti prove',
    severity: 'Media',
    metric: 'Prove Rifiutate',
    expected: 120,
    actual: 187,
    sparkline: [115, 118, 122, 125, 142, 187, 165],
    explanation:
      'Aumento del 55% nei rifiuti delle prove fotografiche. Causa principale: foto sfocate o incomplete (68% dei casi). Suggerimento: migliorare le istruzioni in-app per la cattura foto.',
    suggestedFilters: ['Tipo: Foto', 'Motivo: Qualità bassa'],
  },
  {
    id: 'anom-003',
    title: 'Drop-off modulo LMS',
    severity: 'Media',
    metric: 'Completamenti LMS',
    expected: 85,
    actual: 62,
    sparkline: [82, 84, 86, 88, 75, 62, 65],
    explanation:
      'Il modulo "Economia Circolare - Parte 2" mostra un tasso di abbandono del 27%, superiore alla media. Gli utenti abbandonano al minuto 8:30, dove c\'è un video di 12 minuti.',
    suggestedFilters: ['Corso: Economia Circolare', 'Modulo: Parte 2'],
  },
]

// Funnel Data
export const getFunnelData = (): FunnelStep[] => [
  { label: 'Attivazione', value: 10000, dropoff: 0 },
  { label: 'Prima Azione', value: 8200, dropoff: 18 },
  { label: 'Prima Prova', value: 6150, dropoff: 25 },
  { label: 'Primo Riscatto', value: 4920, dropoff: 20 },
  { label: 'Primo Utilizzo', value: 3936, dropoff: 20 },
  { label: 'Enrol LMS', value: 2755, dropoff: 30 },
  { label: 'Completamento Corso', value: 1928, dropoff: 30 },
]

// Points Economy Data
export const getPointsEconomyData = (): PointsEconomy => ({
  diamond: {
    earned: 45230,
    spent: 32780,
    net: 12450,
    trend: generateTrendData(30, 1200, 200),
  },
  star: {
    earned: 28450,
    spent: 19530,
    net: 8920,
    trend: generateTrendData(30, 850, 150),
  },
  topSources: [
    { label: 'Azioni Quotidiane', value: 18500 },
    { label: 'Prove Approvate', value: 12300 },
    { label: 'Challenge Completate', value: 8900 },
    { label: 'Referral', value: 5530 },
  ],
  topSinks: [
    { label: 'Riscatti Premi', value: 28400 },
    { label: 'Boost Challenge', value: 4380 },
    { label: 'Unlock Contenuti', value: 3200 },
  ],
})

// Proofs Pipeline Data
export const getProofsPipelineData = (): ProofsPipeline => ({
  submitted: 3847,
  inReview: 542,
  approved: 3105,
  rejected: 200,
  avgReviewTime: '4h 23m',
  rejectionReasons: [
    { reason: 'Foto sfocata/incompleta', value: 136 },
    { reason: 'Prodotto non riconoscibile', value: 42 },
    { reason: 'Scontrino non valido', value: 22 },
  ],
  trend: generateTrendData(30, 120, 30).map((d) => ({
    date: d.date,
    submitted: d.value,
    approved: Math.round(d.value * 0.81),
    rejected: Math.round(d.value * 0.05),
  })),
})

// Rewards Performance Data
export const getRewardsPerformanceData = (): RewardPerformance => ({
  claims: 1234,
  utilizations: 892,
  conversionRate: 72.3,
  topRewards: [
    { name: 'Buono Sconto 20€ NaturaSì', claims: 312, utilizations: 287 },
    { name: 'Siero Acido Ialuronico', claims: 234, utilizations: 198 },
    { name: 'Kit Cosmetici Solidi', claims: 187, utilizations: 142 },
  ],
  topPartners: [
    { name: 'NaturaSì', conversionRate: 92 },
    { name: 'La Saponaria', conversionRate: 85 },
    { name: 'BioSapori', conversionRate: 78 },
  ],
  lowPerforming: [
    { name: 'Voucher EcoCasa 15€', issue: 'Bassa disponibilità' },
    { name: 'Workshop Sostenibilità', issue: 'Descrizione poco chiara' },
  ],
})

// Partner Performance Data
export const getPartnerPerformanceData = (): PartnerPerformance => ({
  rankings: [
    { name: 'NaturaSì', score: 94, trend: 5 },
    { name: 'La Saponaria', score: 89, trend: 3 },
    { name: 'BioSapori', score: 82, trend: -2 },
    { name: 'EcoCasa', score: 76, trend: -5 },
    { name: 'GreenMarket', score: 71, trend: 8 },
  ],
  actions: [
    {
      partner: 'EcoCasa',
      action: 'Aumenta disponibilità premi',
      priority: 'Alta',
    },
    {
      partner: 'GreenMarket',
      action: 'Migliora descrizioni prodotti',
      priority: 'Media',
    },
    { partner: 'BioSapori', action: 'Aggiungi foto premi', priority: 'Media' },
  ],
  trend: generateTrendData(30, 82, 8),
})

// LMS Pulse Data
export const getLMSPulseData = (): LMSPulse => ({
  activeUsers: 2755,
  completions: 567,
  passRate: 82.4,
  dropoffModules: [
    {
      module: 'Economia Circolare - Parte 2',
      dropoffRate: 27,
      enrollments: 420,
    },
    { module: 'Riciclo Avanzato', dropoffRate: 22, enrollments: 380 },
    { module: 'Sostenibilità Aziendale', dropoffRate: 18, enrollments: 290 },
  ],
  trend: generateTrendData(30, 550, 80),
})

// Community Data
export const getCommunityData = (): CommunityData => ({
  posts: 1847,
  comments: 5420,
  reactions: 12380,
  hotTopics: [
    { tag: 'zero-waste', count: 342 },
    { tag: 'riciclo-creativo', count: 287 },
    { tag: 'mobilità-sostenibile', count: 234 },
    { tag: 'alimentazione-bio', count: 198 },
    { tag: 'energia-rinnovabile', count: 156 },
  ],
})

// Events Data
export const getEventsData = (): EventsData => ({
  participation: 3420,
  completionRate: 68.5,
  upcomingRegistrations: 892,
  bestChallenges: [
    { name: 'Plastic Free Week', participants: 1240, completionRate: 78 },
    { name: 'Bike to Work', participants: 890, completionRate: 72 },
    { name: 'Zero Waste Challenge', participants: 650, completionRate: 65 },
  ],
})

// Retention Data
export const getRetentionData = (): RetentionData => ({
  wau: 3240,
  mau: 8920,
  d7Retention: 68.5,
  d30Retention: 42.3,
  overlap: {
    appOnly: 2840,
    lmsOnly: 420,
    rewardsOnly: 180,
    appLms: 1240,
    appRewards: 890,
    lmsRewards: 320,
    all: 650,
  },
  cohortTrend: generateTrendData(12, 65, 10),
})

// Data Freshness
export const getDataFreshnessData = (): DataFreshness => ({
  sources: [
    { name: 'App Mobile', lastUpdate: '2 min fa', status: 'ok', coverage: 98 },
    {
      name: 'LMS Platform',
      lastUpdate: '15 min fa',
      status: 'ok',
      coverage: 95,
    },
    {
      name: 'Partners API',
      lastUpdate: '1h 23m fa',
      status: 'warning',
      coverage: 87,
    },
    {
      name: 'Eventi & Challenge',
      lastUpdate: '5 min fa',
      status: 'ok',
      coverage: 100,
    },
  ],
})
